import { Card, colors } from 'plume-ui'
import * as React from 'react'
import { useUser } from '../../../../../contexts/AuthContext'
import { isPrivate, isResearchContinue } from '@/lib/company'
import { formatDate } from '@/lib/date'
import { isEnterpriseWithTrial, isGuest } from '@/lib/user'
import { IGNORED_BY_WOVN_CLASS_NAME } from '@/lib/wovn'
import { CompanyData } from '../../../../../types/api'
import { A } from '../../../../atoms/A'
import { CardTitle } from '../../../../atoms/CardTitle'
import { DescriptionList } from '../../../../atoms/DescriptionList'
import { SNSLinks } from '../../../../atoms/SNSLinks'
import { DoubleColumnContent } from '../../../investor/DoubleColumnContent'
import { DatasourceLink } from '../DatasourceLink'
import { EnterpriseBasicInfo } from '../EnterpriseBasicInfo'
import { GuestBasicInfo } from '../GuestBasicInfo'
import { FormattedDate } from '../../../../atoms/FormattedDate'

type Props = {
  company: CompanyData
  smartroundUrl: string | null
}

export const CompanyBasicInfo: React.FC<Props> = (props) => {
  const user = useUser()

  return (
    <>
      <CardTitle
        right={
          <DatasourceLink href="https://help.initial.inc/ja/articles/5732414" />
        }
        sub={
          isResearchContinue(props.company) ? (
            <>
              <span className={IGNORED_BY_WOVN_CLASS_NAME}>
                <FormattedDate>
                  {props.company.basicInfoUpdatedAt}
                </FormattedDate>
              </span>
              更新
            </>
          ) : (
            ''
          )
        }
      >
        企業概要
      </CardTitle>
      <Card className="overview">
        <DoubleColumnContent>
          <DescriptionList
            list={{
              企業名: { text: props.company.name },
              英語名: { text: props.company.englishName },
              代表者名: { text: props.company.representative },
              住所: { text: props.company.address },
              ...(isEnterpriseWithTrial(user) && {
                電話番号: { text: props.company.telephone },
              }),
              URL: {
                text: props.company.website && (
                  <A
                    href={props.company.website}
                    target="_blank"
                    rel="noopener"
                    decoration="none"
                  >
                    {props.company.website}
                  </A>
                ),
              },
              設立: {
                text:
                  props.company.establishedAt &&
                  formatDate(props.company.establishedAt, 'yyyy/MM'),
              },
              ...(props.company.origin && {
                起源: {
                  text: props.company.origin,
                },
              }),
              ...(props.company.university && {
                '大学発（大学名）': {
                  text: props.company.university,
                },
              }),
              ...(props.company.registeredAddress && {
                本店登記所在地: {
                  text: props.company.registeredAddress,
                },
              }),
              ...(props.company.parentCompany && {
                親会社: {
                  text: props.company.parentCompany.name,
                },
              }),
            }}
          />
          {isPrivate(props.company) ? (
            <DescriptionList
              list={{
                タイプ: { text: props.company.recordType },
                業種: { text: props.company.industry },
                株主状況: { text: props.company.shareholderStatus },
                smartround: {
                  text: props.smartroundUrl ? (
                    <A
                      href={props.smartroundUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      decoration="none"
                    >
                      {props.smartroundUrl}
                    </A>
                  ) : null,
                  extraClassName: 'smartround-url',
                  help: 'smartroundについて',
                  helpLink: 'https://help.initial.inc/ja/articles/6384970',
                  isHidden: props.smartroundUrl == null,
                },
                SNS: {
                  text: (
                    <SNSLinks
                      facebook={props.company.facebookUrl}
                      twitter={props.company.twitterUrl}
                      color={colors.black10}
                      iconSize={20}
                    />
                  ),
                },
              }}
            />
          ) : (
            <DescriptionList
              list={{
                タイプ: { text: props.company.recordType },
                業種: { text: props.company.industry },
                IPO区分: { text: props.company.ipoCategory },
                IPO年月日: {
                  text:
                    props.company.ipoDate &&
                    formatDate(props.company.ipoDate, 'yyyy/MM/dd'),
                },
                IPO市場: { text: props.company.market },
                市場の移動: { text: props.company.marketMovement },
                証券コード: { text: props.company.tickerSymbol },
                SNS: {
                  text: (
                    <SNSLinks
                      facebook={props.company.facebookUrl}
                      twitter={props.company.twitterUrl}
                      color={colors.black10}
                      iconSize={20}
                    />
                  ),
                },
              }}
            />
          )}
        </DoubleColumnContent>
      </Card>
      {isEnterpriseWithTrial(user) && (
        <EnterpriseBasicInfo company={props.company} />
      )}
      {isGuest(user) && <GuestBasicInfo company={props.company} />}
    </>
  )
}
